import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { useAuth } from "../../lib/auth";
import { puedeEditarEventos, puedeEditarChecklist, puedeOperarNegocio, esOrganizadorDeEvento } from "../../lib/permisos";
import { eventosApi } from "../../lib/api";
import {
  Plus, Users, CheckCircle2, Clock, X, Upload, UserCheck, UserX,
  ChevronRight, Calendar, Download, Eye, ArrowLeft, AlertCircle,
  FileSpreadsheet, Trash2, Pencil, Settings, ClipboardList, Lock, Save,
  ChevronUp, ChevronDown,
} from "lucide-react";

interface Invitado {
  id: number; nombre: string; email?: string; telefono?: string;
  notas?: string; acompanantes?: number;
  estado: "pendiente" | "llegó" | "no_show"; llegadaAt?: string;
}
interface ChecklistItem {
  id: string; item: string;
  estado?: string; hecho?: boolean; // hecho = compat con eventos viejos
  responsable?: string; fechaEntrega?: string;
  completadoPor?: string; completadoAt?: string; orden?: number;
}

const CHECK_ESTADOS = [
  { value: "pendiente",       label: "Pendiente",          color: "#94A3B8", bg: "#F1F5F9" },
  { value: "en_proceso",      label: "En proceso",         color: "#2563EB", bg: "#DBEAFE" },
  { value: "espera_cliente",  label: "Espera de cliente",  color: "#D97706", bg: "#FEF3C7" },
  { value: "espera_interna",  label: "Espera interna",     color: "#7C3AED", bg: "#EDE9FE" },
  { value: "culminado",       label: "Culminado",          color: "#059669", bg: "#D1FAE5" },
];
function estadoCheck(c: ChecklistItem): string {
  return c.estado ?? (c.hecho ? "culminado" : "pendiente");
}
interface Evento {
  id: number; nombre: string; sucursalId: number; zonaId?: number;
  fecha: string; horaInicio: string; horaFin?: string;
  organizadorNombre?: string; organizadorEmail?: string; organizadorTelefono?: string;
  notas?: string; observaciones?: string; notasInternas?: string;
  checklist?: ChecklistItem[]; estado: string; totalInvitados: number;
  invitados?: Invitado[];
}
interface Zona { id: number; nombre: string; }
interface InvRow { nombre: string; email: string; telefono: string; acompanantes: number; notas: string; }

const SUCURSALES_DEFAULT = [
  { id: 1, nombre: "Francelina" }, { id: 2, nombre: "Attimo" },
  { id: 3, nombre: "Mam" }, { id: 4, nombre: "Portarossa" },
];

function hoyISO() { return new Date().toISOString().slice(0, 10); }
function fmtFecha(f: string) {
  return new Date(f.slice(0,10) + "T00:00:00").toLocaleDateString("es-VE", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}
function parsearTexto(texto: string): InvRow[] {
  const lines = texto.split("\n").map(l => l.trim()).filter(Boolean);
  const primera = lines[0]?.toLowerCase() ?? "";
  const tieneHeader = primera.includes("nombre") || primera.includes("name") || primera.includes("invitado");
  const datos = tieneHeader ? lines.slice(1) : lines;
  return datos.map(line => {
    const sep = line.includes("\t") ? "\t" : line.includes(";") ? ";" : line.includes("|") ? "|" : ",";
    const parts = line.split(sep).map(p => p.trim().replace(/^["']|["']$/g, ""));
    return {
      nombre: parts[0] ?? "", email: parts[1] ?? "", telefono: parts[2] ?? "",
      acompanantes: parseInt(parts[3] ?? "0") || 0, notas: parts[4] ?? "",
    };
  }).filter(r => r.nombre.length > 1);
}
function descargarTemplate() {
  const csv = "Nombre,Email,Teléfono,Acompañantes,Notas\n" + [
    "María García,maria@email.com,0412-1234567,1,Vegetariana",
    "Carlos López,,0414-9876543,0,",
    "Ana Rodríguez,ana@correo.com,,2,Mesa junto a la ventana",
  ].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url; a.download = "plantilla-invitados.csv"; a.click();
  URL.revokeObjectURL(url);
}

/**
 * Definido fuera de EventosPage a propósito: un componente declarado dentro
 * del cuerpo de otro componente se recrea (nueva identidad de función) en
 * cada render del padre, así que React lo desmonta y monta de nuevo en vez
 * de reutilizarlo -- el textarea pierde el foco después de cada tecla.
 */
function Importador({
  rawText, setRawText, showPreview, setShowPreview, preview, saving, fileRef, importarArchivo, onImport,
}: {
  rawText: string;
  setRawText: (v: string) => void;
  showPreview: boolean;
  setShowPreview: (v: boolean) => void;
  preview: InvRow[];
  saving: boolean;
  fileRef: React.RefObject<HTMLInputElement>;
  importarArchivo: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onImport: (rows: InvRow[]) => void;
}) {
  return (
    <div className="card p-5 space-y-4 border-2 border-brand-200">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-ink-800">Cargar invitados</h3>
        <button onClick={descargarTemplate} className="flex items-center gap-1.5 text-xs text-brand-600 hover:underline">
          <Download className="size-3.5" /> Descargar plantilla CSV
        </button>
      </div>
      <div className="bg-blue-50 rounded-lg p-3 border border-blue-100 text-xs text-blue-700">
        <p className="font-semibold mb-1 flex items-center gap-1.5"><FileSpreadsheet className="size-4" /> Formato: Nombre, Email, Teléfono, Acompañantes, Notas</p>
        <p className="font-mono text-blue-800">María García, maria@email.com, 0412-111, 1, Vegetariana</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => fileRef.current?.click()}
          className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-ink-300 rounded-xl hover:border-brand-400 hover:bg-brand-50">
          <Upload className="size-6 text-ink-400" />
          <span className="text-sm font-medium text-ink-600">Subir CSV/Excel</span>
        </button>
        <button onClick={async () => {
          setShowPreview(false);
          try {
            const texto = await navigator.clipboard.readText();
            if (texto) setRawText(texto);
          } catch {
            // Sin permiso de portapapeles o navegador sin soporte: el usuario
            // igual puede pegar manualmente (Ctrl+V) en el textarea de abajo.
          }
        }}
          className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-ink-300 rounded-xl hover:border-brand-400 hover:bg-brand-50">
          <Users className="size-6 text-ink-400" />
          <span className="text-sm font-medium text-ink-600">Escribir / pegar</span>
        </button>
      </div>
      <input ref={fileRef} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls" className="hidden" onChange={importarArchivo} />
      {!showPreview && (
        <div className="space-y-2">
          <textarea value={rawText} onChange={e => setRawText(e.target.value)} rows={6}
            className="input w-full text-sm font-mono resize-y"
            placeholder={"Nombre, Email, Teléfono, Acompañantes, Notas\nMaría García, maria@email.com, 0412-111, 1, Vegetariana"} />
          {preview.length > 0 && (
            <button onClick={() => setShowPreview(true)} className="btn-secondary text-sm flex items-center gap-2">
              <Eye className="size-4" /> Vista previa ({preview.length})
            </button>
          )}
        </div>
      )}
      {showPreview && preview.length > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-ink-700">{preview.length} invitados ({preview.reduce((a,r)=>a+1+(r.acompanantes||0),0)} personas)</p>
            <button onClick={() => setShowPreview(false)} className="text-xs text-brand-600 hover:underline">Editar</button>
          </div>
          <div className="max-h-56 overflow-y-auto border border-ink-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-ink-50 sticky top-0"><tr className="text-ink-600 font-semibold">
                <th className="px-2 py-1.5 text-left">Nombre</th><th className="px-2 py-1.5 text-left">Email</th>
                <th className="px-2 py-1.5 text-left">Tel</th><th className="px-2 py-1.5 text-center">+</th>
              </tr></thead>
              <tbody>{preview.map((r, i) => (
                <tr key={i} className="border-t border-ink-100">
                  <td className="px-2 py-1 font-medium">{r.nombre}</td>
                  <td className="px-2 py-1 text-ink-500 truncate max-w-[100px]">{r.email||"—"}</td>
                  <td className="px-2 py-1 text-ink-500">{r.telefono||"—"}</td>
                  <td className="px-2 py-1 text-center">{r.acompanantes||"—"}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="flex gap-3">
            <button onClick={() => { setShowPreview(false); setRawText(""); }} className="btn-secondary flex-1 text-sm">Cancelar</button>
            <button onClick={() => onImport(preview)} disabled={saving} className="btn-primary flex-1 text-sm">
              {saving ? "..." : `Importar ${preview.length}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function EventosPage() {
  const { token, user } = useAuth();

  const [eventos, setEventos] = useState<Evento[]>([]);
  const [usuariosCRM, setUsuariosCRM] = useState<{id: string; fullName: string; role: string}[]>([]);
  const [eventoSel, setEventoSel] = useState<Evento | null>(null);

  // Si el correo del usuario coincide con el organizador cargado en ESTE
  // evento puntual, tiene los mismos derechos sobre él aunque su rol no
  // sea administrativo/supervisor/gerencia (espejo del backend).
  const esOrganizadorDelSel = esOrganizadorDeEvento(user, eventoSel);
  const puedeEditar = puedeEditarEventos(user) || esOrganizadorDelSel;       // editar datos del evento
  const puedeOperar = puedeOperarNegocio(user) || esOrganizadorDelSel;       // check-in, marcar llegadas, agregar/quitar inv.
  const puedeChecklist = puedeEditarChecklist(user) || esOrganizadorDelSel;  // ver/editar el checklist completo (no solo lo propio)
  const [showForm, setShowForm] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [editando, setEditando] = useState(false);
  const [sucursales, setSucursales] = useState(SUCURSALES_DEFAULT);
  const [zonas, setZonas] = useState<Zona[]>([]);
  const [sucursalFiltro, setSucursalFiltro] = useState(1);
  const [buscando, setBuscando] = useState("");
  const [saving, setSaving] = useState(false);
  const [addingGuests, setAddingGuests] = useState(false);
  const [showImportBulk, setShowImportBulk] = useState(false);
  const [rawText, setRawText] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const NUEVO_INV_EMPTY = { nombre: '', telefono: '', email: '', acompanantes: 0, notas: '' };
  const [nuevoInv, setNuevoInv] = useState({ ...NUEVO_INV_EMPTY });
  const [addingError, setAddingError] = useState<string | null>(null);

  // Config (tags y checklist personalizables)
  const [tags, setTags] = useState<string[]>([]);
  const [checklistTpl, setChecklistTpl] = useState<string[]>([]);
  const [editTags, setEditTags] = useState<string[]>([]);
  const [editChecklist, setEditChecklist] = useState<string[]>([]);
  const [nuevoTag, setNuevoTag] = useState("");
  const [nuevoCheck, setNuevoCheck] = useState("");
  // Observaciones manuales (no son parte del preset de tags configurado)
  const [nuevaObsDetalle, setNuevaObsDetalle] = useState("");
  const [nuevaObsForm, setNuevaObsForm] = useState("");

  const FORM_EMPTY = {
    nombre: "", sucursalId: 1, zonaId: undefined as number | undefined,
    fecha: hoyISO(), horaInicio: "18:00", horaFin: "22:00",
    organizadorNombre: "", organizadorEmail: "", organizadorTelefono: "",
    notas: "", observaciones: "", notasInternas: "",
  };
  const [form, setForm] = useState({ ...FORM_EMPTY });
  const [invitadosTexto, setInvitadosTexto] = useState("");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const preview = parsearTexto(rawText);

  useEffect(() => {
    fetch("/api/v1/sucursales").then(r => r.json())
      .then(d => Array.isArray(d) && setSucursales(d.map((s: any) => ({ id: s.id, nombre: s.nombre }))))
      .catch(() => {});
    eventosApi.getConfig().then(c => {
      setTags(c.tagsObservaciones ?? []);
      setChecklistTpl(c.checklistTemplate ?? []);
    }).catch(() => {});
    // Cargar usuarios CRM activos para asignar en checklist (endpoint accesible a todos)
    fetch("/api/v1/users/activos", { headers })
      .then(r => r.ok ? r.json() : [])
      .then((d: any) => {
        setUsuariosCRM(Array.isArray(d) ? d : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/v1/sucursales/${sucursalFiltro}/zonas`, { headers })
      .then(r => r.json()).then(setZonas).catch(() => setZonas([]));
  }, [sucursalFiltro, token]);

  const cargar = () => {
    if (!token) return;
    eventosApi.list({ sucursalId: sucursalFiltro, desde: hoyISO() })
      .then(setEventos).catch(() => {});
  };
  useEffect(() => { cargar(); }, [sucursalFiltro, token]);

  const abrirEvento = async (id: number) => {
    const data = await eventosApi.get(id);
    setEventoSel(data); setEditando(false);
  };

  // Deep-link desde la campanita de notificaciones (?evento=123)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const eventoId = searchParams.get("evento");
    if (eventoId) {
      abrirEvento(+eventoId);
      setSearchParams(prev => { prev.delete("evento"); return prev; }, { replace: true });
    }
  }, []);

  const crearEvento = async () => {
    if (!form.nombre || !form.fecha) return;
    setSaving(true);
    try {
      const invitados = parsearTexto(invitadosTexto);
      const data = await eventosApi.create({ ...form, invitados });
      setShowForm(false); setForm({ ...FORM_EMPTY }); setInvitadosTexto("");
      cargar(); abrirEvento(data.id);
    } finally { setSaving(false); }
  };

  const guardarEdicion = async () => {
    if (!eventoSel) return;
    setSaving(true);
    try {
      await eventosApi.update(eventoSel.id, {
        nombre: eventoSel.nombre, fecha: eventoSel.fecha, zonaId: eventoSel.zonaId,
        horaInicio: eventoSel.horaInicio, horaFin: eventoSel.horaFin,
        organizadorNombre: eventoSel.organizadorNombre,
        organizadorTelefono: eventoSel.organizadorTelefono,
        organizadorEmail: eventoSel.organizadorEmail,
        notas: eventoSel.notas, observaciones: eventoSel.observaciones,
        notasInternas: eventoSel.notasInternas,
      });
      await abrirEvento(eventoSel.id);
      setEditando(false); cargar();
    } finally { setSaving(false); }
  };

  const updateCheckItem = async (itemId: string, patch: { estado?: string; responsable?: string; usuarioId?: string | null; fechaEntrega?: string }) => {
    if (!eventoSel) return;
    const body: any = { itemId, ...patch };
    if (patch.estado === "culminado") body.completadoPor = user?.fullName ?? user?.email ?? "—";
    const checklist = await eventosApi.updateChecklistItem(eventoSel.id, body) as ChecklistItem[];
    setEventoSel({ ...eventoSel, checklist });
  };

  const moverCheck = async (idx: number, dir: -1 | 1) => {
    if (!eventoSel) return;
    const lista = [...(eventoSel.checklist ?? [])];
    const ni = idx + dir;
    if (ni < 0 || ni >= lista.length) return;
    [lista[idx], lista[ni]] = [lista[ni], lista[idx]];
    setEventoSel({ ...eventoSel, checklist: lista });
    await eventosApi.saveChecklist(eventoSel.id, lista);
  };

  const checkIn = async (invId: number, detalle?: { observacionLlegada?: string; acompanantesReales?: number | null }) => {
    await eventosApi.checkIn(invId, detalle);
    if (eventoSel) await abrirEvento(eventoSel.id);
  };
  const [checkinDetalleId, setCheckinDetalleId] = useState<number | null>(null);
  const [checkinObs, setCheckinObs] = useState("");
  const [checkinAcomp, setCheckinAcomp] = useState<string>("");
  const undoCheckIn = async (invId: number) => { await eventosApi.undoCheckIn(invId); if (eventoSel) await abrirEvento(eventoSel.id); };
  const noShow = async (invId: number) => { await eventosApi.noShow(invId); if (eventoSel) await abrirEvento(eventoSel.id); };
  const quitarInvitado = async (invId: number) => {
    if (!confirm("¿Quitar este invitado?")) return;
    await eventosApi.removeInvitado(invId); if (eventoSel) await abrirEvento(eventoSel.id);
  };

  const agregarUno = async () => {
    if (!nuevoInv.nombre.trim()) { setAddingError("El nombre es requerido"); return; }
    if (!eventoSel) return;
    setAddingError(null);
    setSaving(true);
    try {
      await fetch(`/api/v1/eventos/${eventoSel.id}/invitados`, {
        method: 'POST', headers,
        body: JSON.stringify({
          nombre: nuevoInv.nombre.trim(),
          telefono: nuevoInv.telefono.trim() || undefined,
          email: nuevoInv.email.trim() || undefined,
          acompanantes: Number(nuevoInv.acompanantes) || 0,
          notas: nuevoInv.notas.trim() || undefined,
        }),
      });
      setNuevoInv({ ...NUEVO_INV_EMPTY });
      setAddingError(null);
      await abrirEvento(eventoSel.id);
    } catch (e: any) {
      setAddingError(e.message || 'Error al agregar invitado');
    } finally { setSaving(false); }
  };

  const importarArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const esExcel = /\.xlsx?$/i.test(file.name);
    const reader = new FileReader();
    if (esExcel) {
      reader.onload = ev => {
        try {
          const data = new Uint8Array(ev.target?.result as ArrayBuffer);
          const libro = XLSX.read(data, { type: "array" });
          const hoja = libro.Sheets[libro.SheetNames[0]];
          const csv = XLSX.utils.sheet_to_csv(hoja, { FS: "," });
          setRawText(csv);
          setShowPreview(true);
        } catch {
          window.alert("No se pudo leer el archivo Excel. Probá exportarlo como CSV.");
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = ev => { setRawText(ev.target?.result as string); setShowPreview(true); };
      reader.readAsText(file, "utf-8");
    }
    e.target.value = "";
  };

  const guardarConfig = async () => {
    setSaving(true);
    try {
      await eventosApi.saveConfig({ tagsObservaciones: editTags, checklistTemplate: editChecklist });
      setTags(editTags); setChecklistTpl(editChecklist); setShowConfig(false);
    } finally { setSaving(false); }
  };

  const filtrados = eventos.filter(ev =>
    ev.nombre.toLowerCase().includes(buscando.toLowerCase()) ||
    (ev.organizadorNombre ?? "").toLowerCase().includes(buscando.toLowerCase())
  );

  // ════════ VISTA DETALLE ════════
  if (eventoSel) {
    const inv = eventoSel.invitados ?? [];
    const llegaron = inv.filter(i => i.estado === "llegó");
    const pendientes = inv.filter(i => i.estado === "pendiente");
    const noShows = inv.filter(i => i.estado === "no_show");
    const totalPersonas = inv.reduce((a, i) => a + 1 + (i.acompanantes ?? 0), 0);
    const llegadosPersonas = llegaron.reduce((a, i) => a + 1 + (i.acompanantes ?? 0), 0);
    const pct = totalPersonas > 0 ? Math.round((llegadosPersonas / totalPersonas) * 100) : 0;
    const esFinalizado = eventoSel.estado === "finalizado";
    const checklist = eventoSel.checklist ?? [];
    const checkHechos = checklist.filter(c => estadoCheck(c) === "culminado").length;

    return (
      <div className="mx-auto max-w-3xl px-6 py-8 space-y-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <button onClick={() => { setEventoSel(null); setEditando(false); }} className="mt-1 p-1.5 rounded-lg hover:bg-ink-100 text-ink-400">
            <ArrowLeft className="size-5" />
          </button>
          <div className="flex-1">
            {editando ? (
              <input value={eventoSel.nombre} onChange={e => setEventoSel({ ...eventoSel, nombre: e.target.value })}
                className="input text-xl font-bold w-full mb-2" />
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-2xl font-bold text-ink-800">{eventoSel.nombre}</h1>
                <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">{eventoSel.estado}</span>
              </div>
            )}
            {editando ? (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div><label className="text-xs text-ink-500">Fecha</label>
                  <input type="date" value={eventoSel.fecha?.slice(0,10)} onChange={e => setEventoSel({ ...eventoSel, fecha: e.target.value })} className="input w-full text-sm" /></div>
                <div><label className="text-xs text-ink-500">Zona</label>
                  <select value={eventoSel.zonaId ?? ""} onChange={e => setEventoSel({ ...eventoSel, zonaId: e.target.value ? +e.target.value : undefined })} className="input w-full text-sm">
                    <option value="">Sin zona</option>
                    {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                  </select></div>
                <div><label className="text-xs text-ink-500">Hora inicio</label>
                  <input type="time" value={eventoSel.horaInicio} onChange={e => setEventoSel({ ...eventoSel, horaInicio: e.target.value })} className="input w-full text-sm" /></div>
                <div><label className="text-xs text-ink-500">Hora fin</label>
                  <input type="time" value={eventoSel.horaFin ?? ""} onChange={e => setEventoSel({ ...eventoSel, horaFin: e.target.value })} className="input w-full text-sm" /></div>
              </div>
            ) : (
              <div className="flex items-center gap-4 mt-1 text-sm text-ink-500 flex-wrap">
                <span className="flex items-center gap-1"><Calendar className="size-3.5" /> {fmtFecha(eventoSel.fecha)}</span>
                <span className="flex items-center gap-1"><Clock className="size-3.5" /> {eventoSel.horaInicio}{eventoSel.horaFin ? ` – ${eventoSel.horaFin}` : ""}</span>
                {eventoSel.zonaId && <span className="flex items-center gap-1 font-medium text-brand-600">📍 {zonas.find(z => z.id === eventoSel.zonaId)?.nombre ?? `Zona ${eventoSel.zonaId}`}</span>}
              </div>
            )}
          </div>
          {/* Badge finalizado */}
          {eventoSel.estado === "finalizado" && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold shrink-0">
              <Lock className="size-3" /> Finalizado
            </span>
          )}
          {/* Botón editar (solo admin/supervisor, no finalizado) */}
          {puedeEditar && !editando && eventoSel.estado !== "finalizado" && (
            <button onClick={() => setEditando(true)} className="btn-secondary text-sm flex items-center gap-1.5 shrink-0">
              <Pencil className="size-3.5" /> Editar
            </button>
          )}
          {editando && (
            <div className="flex gap-2 shrink-0">
              <button onClick={() => abrirEvento(eventoSel.id)} className="btn-secondary text-sm">Cancelar</button>
              <button onClick={guardarEdicion} disabled={saving} className="btn-primary text-sm flex items-center gap-1.5">
                <Save className="size-3.5" /> {saving ? "..." : "Guardar"}
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total personas", value: totalPersonas, color: "text-ink-800", bg: "bg-white" },
            { label: "Llegaron", value: llegadosPersonas, color: "text-emerald-700", bg: "bg-emerald-50" },
            { label: "Pendientes", value: pendientes.length, color: "text-amber-700", bg: "bg-amber-50" },
            { label: "No vinieron", value: noShows.length, color: "text-rose-600", bg: "bg-rose-50" },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center border border-ink-100`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-ink-500">{s.label}</p>
            </div>
          ))}
        </div>
        {totalPersonas > 0 && (
          <div>
            <div className="flex justify-between text-xs text-ink-500 mb-1">
              <span>Asistencia</span><span className="font-semibold">{llegadosPersonas}/{totalPersonas} ({pct}%)</span>
            </div>
            <div className="h-3 bg-ink-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        {/* CHECKLIST DE PREPARACIÓN */}
        {checklist.length > 0 && (
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-ink-800 flex items-center gap-2">
                <ClipboardList className="size-4 text-brand-600" /> Checklist de preparación
              </h3>
              <span className={`text-sm font-bold ${checkHechos === checklist.length ? "text-emerald-600" : "text-amber-600"}`}>
                {checkHechos}/{checklist.length}
              </span>
            </div>
            <div className="space-y-2">
              {checklist.map((c, idx) => {
                const est = estadoCheck(c);
                const estInfo = CHECK_ESTADOS.find(e => e.value === est) ?? CHECK_ESTADOS[0];
                const culminado = est === "culminado";
                return (
                  <div key={c.id}
                    className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-ink-100"
                    style={{ background: culminado ? "#F0FDF4" : "#fff" }}>
                    {/* Reordenar (solo admin/supervisor) */}
                    {puedeEditar && (
                      <div className="flex flex-col shrink-0 -my-0.5">
                        <button onClick={() => moverCheck(idx, -1)} disabled={idx === 0}
                          className="text-ink-300 hover:text-ink-600 disabled:opacity-20"><ChevronUp className="size-4" /></button>
                        <button onClick={() => moverCheck(idx, 1)} disabled={idx === checklist.length - 1}
                          className="text-ink-300 hover:text-ink-600 disabled:opacity-20"><ChevronDown className="size-4" /></button>
                      </div>
                    )}

                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Nombre del item */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-ink-300">{idx + 1}.</span>
                        <span className={`text-sm font-medium flex-1 ${culminado ? "text-emerald-800" : "text-ink-800"}`}>{c.item}</span>
                      </div>

                      {/* Controles: estado, responsable, fecha */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Estado dropdown — cocina, supervisores, y el usuario asignado a esta tarea */}
                        {(() => { const puedeEstaTarea = puedeChecklist || (c as any).usuarioId === user?.id; return (
                        <select value={est}
                          onChange={e => puedeEstaTarea && eventoSel.estado !== "finalizado" && updateCheckItem(c.id, { estado: e.target.value })}
                          disabled={!puedeEstaTarea || eventoSel.estado === "finalizado"}
                          className="text-xs font-semibold rounded-lg px-2.5 py-1.5 border-0 cursor-pointer focus:ring-2 focus:ring-offset-1 disabled:opacity-70 disabled:cursor-default"
                          style={{ background: estInfo.bg, color: estInfo.color }}>
                          {CHECK_ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                        </select>
                        ); })()}

                        {/* Usuario CRM asignado */}
                        {puedeChecklist && (
                          <select
                            value={(c as any).usuarioId ?? ""}
                            onChange={e => updateCheckItem(c.id, { usuarioId: e.target.value || null })}
                            className="text-xs px-2 py-1.5 border border-ink-200 rounded-lg max-w-[150px] text-ink-700"
                            title="Asignar a usuario del CRM">
                            <option value="">👤 Sin asignar</option>
                            {usuariosCRM.map(u => (
                              <option key={u.id} value={u.id}>{u.fullName}</option>
                            ))}
                          </select>
                        )}
                        {!puedeChecklist && (c as any).usuarioId && (
                          <span className="text-xs px-2 py-1 bg-ink-100 rounded-lg text-ink-600">
                            👤 {usuariosCRM.find(u => u.id === (c as any).usuarioId)?.fullName ?? 'Asignado'}
                          </span>
                        )}

                        {/* Responsable texto libre */}
                        <input
                          value={c.responsable ?? ""}
                          onChange={e => setEventoSel(prev => prev ? { ...prev, checklist: prev.checklist!.map(x => x.id === c.id ? { ...x, responsable: e.target.value } : x) } : prev)}
                          onBlur={e => updateCheckItem(c.id, { responsable: e.target.value })}
                          placeholder="📋 Área / Rol"
                          className="text-xs px-2 py-1.5 border border-ink-200 rounded-lg w-28" />

                        {/* Fecha de entrega */}
                        <input type="datetime-local"
                          value={c.fechaEntrega ? c.fechaEntrega.slice(0, 16) : ""}
                          onChange={e => updateCheckItem(c.id, { fechaEntrega: e.target.value })}
                          className="text-xs px-2 py-1.5 border border-ink-200 rounded-lg text-ink-500" />
                      </div>

                      {/* Quién culminó */}
                      {culminado && c.completadoPor && (
                        <p className="text-xs text-emerald-600 flex items-center gap-1">
                          <CheckCircle2 className="size-3" />
                          Culminado por <strong>{c.completadoPor}</strong>
                          {c.completadoAt && ` · ${new Date(c.completadoAt).toLocaleString("es-VE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`}
                        </p>
                      )}
                      {/* Observación */}
                      {(c as any).observacion && (
                        <p className="text-xs text-ink-500 italic">{(c as any).observacion}</p>
                      )}
                      {/* Fecha de entrega vencida warning */}
                      {!culminado && c.fechaEntrega && new Date(c.fechaEntrega) < new Date() && (
                        <p className="text-xs text-rose-500 flex items-center gap-1">
                          <AlertCircle className="size-3" /> Entrega vencida
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Datos del organizador y observaciones */}
        <div className="card p-5 space-y-3">
          {editando ? (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs text-ink-500">Organizador</label>
                  <input value={eventoSel.organizadorNombre ?? ""} onChange={e => setEventoSel({ ...eventoSel, organizadorNombre: e.target.value })} className="input w-full text-sm" /></div>
                <div><label className="text-xs text-ink-500">Teléfono</label>
                  <input value={eventoSel.organizadorTelefono ?? ""} onChange={e => setEventoSel({ ...eventoSel, organizadorTelefono: e.target.value })} className="input w-full text-sm" /></div>
                <div><label className="text-xs text-ink-500">Email</label>
                  <input value={eventoSel.organizadorEmail ?? ""} onChange={e => setEventoSel({ ...eventoSel, organizadorEmail: e.target.value })} className="input w-full text-sm" /></div>
              </div>
              <div><label className="text-xs text-ink-500">Nota del evento</label>
                <input value={eventoSel.notas ?? ""} onChange={e => setEventoSel({ ...eventoSel, notas: e.target.value })} className="input w-full text-sm" /></div>
              <div>
                <label className="text-xs text-ink-500">Observaciones (tags)</label>
                <div className="flex flex-wrap gap-1.5 mt-1 mb-2">
                  {tags.map(t => {
                    const sel = (eventoSel.observaciones ?? "").includes(t);
                    return (
                      <button key={t} onClick={() => {
                        const obs = eventoSel.observaciones ?? "";
                        const nueva = sel ? obs.split("\n").filter(o => o !== t).join("\n") : (obs ? obs + "\n" + t : t);
                        setEventoSel({ ...eventoSel, observaciones: nueva });
                      }}
                        className={`text-xs px-2.5 py-1 rounded-full border ${sel ? "bg-brand-600 text-white border-brand-600" : "border-ink-300 text-ink-600 hover:border-brand-400"}`}>
                        {t}
                      </button>
                    );
                  })}
                  {(eventoSel.observaciones ?? "").split("\n").map(o => o.trim()).filter(o => o && !tags.includes(o)).map((o, i) => (
                    <button key={`custom-${i}-${o}`} onClick={() => {
                      const nueva = (eventoSel.observaciones ?? "").split("\n").filter(line => line.trim() !== o).join("\n");
                      setEventoSel({ ...eventoSel, observaciones: nueva });
                    }}
                      className="text-xs px-2.5 py-1 rounded-full border bg-brand-600 text-white border-brand-600 inline-flex items-center gap-1">
                      {o} <span className="opacity-70">×</span>
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input value={nuevaObsDetalle} onChange={e => setNuevaObsDetalle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter" && nuevaObsDetalle.trim()) {
                        e.preventDefault();
                        const obs = eventoSel.observaciones ?? "";
                        setEventoSel({ ...eventoSel, observaciones: obs ? obs + "\n" + nuevaObsDetalle.trim() : nuevaObsDetalle.trim() });
                        setNuevaObsDetalle("");
                      }
                    }}
                    placeholder="Escribir una observación manual..." className="input flex-1 text-sm" />
                  <button type="button" onClick={() => {
                    if (!nuevaObsDetalle.trim()) return;
                    const obs = eventoSel.observaciones ?? "";
                    setEventoSel({ ...eventoSel, observaciones: obs ? obs + "\n" + nuevaObsDetalle.trim() : nuevaObsDetalle.trim() });
                    setNuevaObsDetalle("");
                  }} className="btn-secondary text-sm px-3">Añadir</button>
                </div>
              </div>
            </>
          ) : (
            <>
              {eventoSel.organizadorNombre && (
                <div className="flex items-center gap-4 text-sm text-ink-500 flex-wrap">
                  <span>👤 {eventoSel.organizadorNombre}</span>
                  {eventoSel.organizadorTelefono && <a href={`https://wa.me/${eventoSel.organizadorTelefono.replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="text-green-600 hover:underline">💬 {eventoSel.organizadorTelefono}</a>}
                  {eventoSel.organizadorEmail && <a href={`mailto:${eventoSel.organizadorEmail}`} className="text-blue-500 hover:underline">✉️ {eventoSel.organizadorEmail}</a>}
                </div>
              )}
              {eventoSel.notas && <p className="text-sm text-ink-500">📌 {eventoSel.notas}</p>}
              {eventoSel.observaciones && (
                <div className="flex flex-wrap gap-1.5">
                  {eventoSel.observaciones.split("\n").filter(Boolean).map((o, i) => (
                    <span key={i} className="px-2 py-0.5 bg-brand-50 text-brand-700 rounded-full text-xs font-medium">{o}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* NOTAS INTERNAS (solo admin/supervisor) */}
        {puedeEditar && (
          <div className="card p-5">
            <h3 className="font-semibold text-ink-800 flex items-center gap-2 mb-2">
              <Lock className="size-4 text-ink-400" /> Notas internas
              <span className="text-xs font-normal text-ink-400">(no visible para recepción)</span>
            </h3>
            {editando ? (
              <textarea value={eventoSel.notasInternas ?? ""} onChange={e => setEventoSel({ ...eventoSel, notasInternas: e.target.value })}
                rows={3} className="input w-full text-sm resize-y" placeholder="Notas para el equipo: detalles de pago, acuerdos especiales, etc." />
            ) : (
              <p className="text-sm text-ink-600 whitespace-pre-wrap">{eventoSel.notasInternas || <span className="text-ink-300 italic">Sin notas internas. Usa "Editar" para agregar.</span>}</p>
            )}
          </div>
        )}

        {/* Invitados */}
        {(() => {
          const puedeGestionarInvitados = puedeOperar && !esFinalizado;
          return (
            <>
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-ink-800">
                  Lista de invitados
                  <span className="ml-2 text-sm font-normal text-ink-500">
                    ({inv.length} {inv.length === 1 ? 'persona' : 'personas'}
                    {inv.reduce((a, i) => a + (i.acompanantes ?? 0), 0) > 0
                      ? ` + ${inv.reduce((a, i) => a + (i.acompanantes ?? 0), 0)} acompañantes` : ''})
                  </span>
                </h2>
                {puedeGestionarInvitados && (
                  <button
                    onClick={() => { setAddingGuests(!addingGuests); setAddingError(null); setShowImportBulk(false); }}
                    className={`text-sm flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium transition-colors ${addingGuests ? 'bg-ink-100 text-ink-600' : 'btn-primary'}`}>
                    {addingGuests ? <><X className="size-4" /> Cerrar</> : <><Plus className="size-4" /> Agregar</>}
                  </button>
                )}
              </div>

              {/* Panel de agregar invitados */}
              {addingGuests && puedeGestionarInvitados && (
                <div className="card p-4 space-y-3 border-2 border-brand-200 bg-brand-50/30">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm text-ink-800 flex items-center gap-2">
                      <Plus className="size-4 text-brand-600" /> Agregar invitado
                    </h3>
                    <button
                      onClick={() => { setShowImportBulk(v => !v); setAddingError(null); }}
                      className="text-xs text-brand-600 hover:text-brand-800 flex items-center gap-1 border border-brand-300 px-2 py-1 rounded-lg hover:bg-brand-100 transition-colors">
                      <FileSpreadsheet className="size-3" />
                      {showImportBulk ? 'Formulario individual' : 'Importar varios (CSV)'}
                    </button>
                  </div>

                  {!showImportBulk ? (
                    /* ── Formulario individual ── */
                    <div className="space-y-3">
                      {addingError && (
                        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                          {addingError}
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs text-ink-500 mb-1">Nombre <span className="text-red-500">*</span></label>
                          <input
                            autoFocus
                            className="input w-full text-sm"
                            placeholder="Nombre completo"
                            value={nuevoInv.nombre}
                            onChange={e => setNuevoInv(v => ({ ...v, nombre: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && agregarUno()}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-ink-500 mb-1">Teléfono</label>
                          <input
                            className="input w-full text-sm"
                            placeholder="0412-000-0000"
                            value={nuevoInv.telefono}
                            onChange={e => setNuevoInv(v => ({ ...v, telefono: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && agregarUno()}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-ink-500 mb-1">Email</label>
                          <input
                            type="email"
                            className="input w-full text-sm"
                            placeholder="opcional"
                            value={nuevoInv.email}
                            onChange={e => setNuevoInv(v => ({ ...v, email: e.target.value }))}
                          />
                        </div>
                        <div className="flex gap-2">
                          <div className="w-28">
                            <label className="block text-xs text-ink-500 mb-1">Acompañantes</label>
                            <input
                              type="number" min={0} max={20}
                              className="input w-full text-sm text-center"
                              value={nuevoInv.acompanantes}
                              onChange={e => setNuevoInv(v => ({ ...v, acompanantes: Number(e.target.value) || 0 }))}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-ink-500 mb-1">Notas</label>
                            <input
                              className="input w-full text-sm"
                              placeholder="Alergias, asiento, etc."
                              value={nuevoInv.notas}
                              onChange={e => setNuevoInv(v => ({ ...v, notas: e.target.value }))}
                              onKeyDown={e => e.key === 'Enter' && agregarUno()}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={agregarUno}
                          disabled={saving || !nuevoInv.nombre.trim()}
                          className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
                          {saving ? '...' : <><Plus className="size-4" /> Agregar invitado</>}
                        </button>
                        <button
                          onClick={() => { setNuevoInv({ ...NUEVO_INV_EMPTY }); setAddingError(null); }}
                          className="btn-secondary text-sm">
                          Limpiar
                        </button>
                      </div>
                      <p className="text-[11px] text-ink-400">Tip: presioná Enter en cualquier campo para agregar rápidamente</p>
                    </div>
                  ) : (
                    /* ── Importador CSV ── */
                    <Importador
                      rawText={rawText} setRawText={setRawText}
                      showPreview={showPreview} setShowPreview={setShowPreview}
                      preview={preview} saving={saving}
                      fileRef={fileRef} importarArchivo={importarArchivo}
                      onImport={async (rows) => {
                        setSaving(true);
                        try {
                          await eventosApi.addBulk(eventoSel.id, rows);
                          await abrirEvento(eventoSel.id);
                          setAddingGuests(false);
                          setShowImportBulk(false);
                          setRawText(""); setShowPreview(false);
                        } finally { setSaving(false); }
                      }} />
                  )}
                </div>
              )}
            </>
          );
        })()}

        <div className="space-y-1">
          {inv.length === 0 ? (
            <div className="text-center py-10 text-ink-400 bg-ink-50 rounded-xl">
              <Users className="size-8 mx-auto mb-2 opacity-30" /><p className="text-sm">Sin invitados aún</p>
            </div>
          ) : (
            <>
              {pendientes.map(i => {
                const enDetalle = checkinDetalleId === i.id;
                return (
                <div key={i.id} className="bg-white rounded-xl border border-ink-100 overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm text-ink-800">{i.nombre}</p>
                        {(i.acompanantes ?? 0) > 0 && <span className="text-xs px-1.5 py-0.5 bg-brand-50 text-brand-600 rounded-full">+{i.acompanantes}</span>}
                      </div>
                      <div className="flex gap-3 text-xs text-ink-400 flex-wrap">
                        {i.telefono && <a href={`tel:${i.telefono}`} className="hover:text-green-600">📞 {i.telefono}</a>}
                        {i.email && <a href={`mailto:${i.email}`} className="hover:text-blue-500 truncate max-w-[150px]">✉️ {i.email}</a>}
                        {i.notas && <span className="italic">📌 {i.notas}</span>}
                      </div>
                    </div>
                    {/* Check-in: solo admin, gerencia, supervisor, recepción */}
                    {puedeOperar && !enDetalle && !esFinalizado && (
                      <div className="flex gap-1.5 shrink-0">
                        <button
                          onClick={() => {
                            setCheckinDetalleId(i.id);
                            setCheckinObs("");
                            setCheckinAcomp(String(i.acompanantes ?? 0));
                          }}
                          className="flex items-center gap-1 text-xs px-3 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 active:scale-95">
                          <UserCheck className="size-3.5" /> Llegó
                        </button>
                        <button onClick={() => noShow(i.id)} className="text-xs px-2.5 py-2 bg-ink-100 text-ink-600 rounded-lg hover:bg-ink-200">No vino</button>
                        <button onClick={() => quitarInvitado(i.id)} title="Quitar invitado" className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="size-3.5" /></button>
                      </div>
                    )}
                  </div>

                  {/* Mini-form de detalle al hacer check-in */}
                  {enDetalle && (
                    <div className="px-4 py-3 bg-emerald-50/40 border-t border-emerald-100 space-y-2">
                      <div className="flex flex-wrap gap-2 items-end">
                        <div className="flex-1 min-w-[140px]">
                          <label className="block text-[10px] uppercase tracking-wide font-semibold text-emerald-800 mb-1">
                            Acompañantes reales
                          </label>
                          <input
                            type="number" min={0} max={20}
                            value={checkinAcomp}
                            onChange={e => setCheckinAcomp(e.target.value)}
                            className="w-full text-sm px-2 py-1.5 rounded-md border border-emerald-200 bg-white focus:border-emerald-500 focus:outline-none"
                          />
                          {String(i.acompanantes ?? 0) !== checkinAcomp && (
                            <p className="text-[10px] text-amber-700 mt-0.5">
                              esperado: {i.acompanantes ?? 0}
                            </p>
                          )}
                        </div>
                        <div className="flex-[3] min-w-[200px]">
                          <label className="block text-[10px] uppercase tracking-wide font-semibold text-emerald-800 mb-1">
                            Observación de llegada (opcional)
                          </label>
                          <input
                            type="text" maxLength={500}
                            value={checkinObs}
                            onChange={e => setCheckinObs(e.target.value)}
                            placeholder="Salió y vuelve · vino con más invitados · inconveniente con..."
                            className="w-full text-sm px-2 py-1.5 rounded-md border border-emerald-200 bg-white focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            await checkIn(i.id, {
                              observacionLlegada: checkinObs.trim() || undefined,
                              acompanantesReales: checkinAcomp !== "" ? Number(checkinAcomp) : null,
                            });
                            setCheckinDetalleId(null);
                          }}
                          className="flex-1 text-xs px-3 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700">
                          Confirmar llegada
                        </button>
                        <button
                          onClick={() => setCheckinDetalleId(null)}
                          className="text-xs px-3 py-2 bg-white text-ink-600 rounded-lg border border-ink-200 hover:bg-ink-50">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
              })}
              {llegaron.map((i: any) => {
                const reales = i.acompanantesReales;
                const difAcomp = reales != null && reales !== (i.acompanantes ?? 0);
                return (
                <div key={i.id} className="flex items-start gap-3 px-4 py-2.5 bg-emerald-50 rounded-xl border border-emerald-100">
                  <CheckCircle2 className="size-5 text-emerald-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm text-emerald-800">{i.nombre}</p>
                      {reales != null
                        ? <span className={`text-xs px-1.5 py-0.5 rounded-full ${difAcomp ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-emerald-100 text-emerald-600"}`}>
                            +{reales}{difAcomp ? ` (esperado: ${i.acompanantes ?? 0})` : ""}
                          </span>
                        : (i.acompanantes ?? 0) > 0 && <span className="text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-600 rounded-full">+{i.acompanantes}</span>}
                    </div>
                    {i.llegadaAt && <p className="text-xs text-emerald-600">✓ {new Date(i.llegadaAt).toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" })}</p>}
                    {i.observacionLlegada && (
                      <p className="text-xs text-amber-800 italic mt-1 px-2 py-1 bg-amber-50 border-l-2 border-amber-300 rounded">
                        📝 {i.observacionLlegada}
                      </p>
                    )}
                  </div>
                  {puedeOperar && !esFinalizado && <button onClick={() => undoCheckIn(i.id)} className="text-xs text-emerald-500 hover:underline px-2 shrink-0 mt-0.5">deshacer</button>}
                </div>
                );
              })}
              {noShows.map(i => (
                <div key={i.id} className="flex items-center gap-3 px-4 py-2.5 bg-ink-50 rounded-xl border border-ink-100 opacity-50">
                  <UserX className="size-4 text-ink-400 shrink-0" />
                  <p className="flex-1 text-sm text-ink-500 line-through">{i.nombre}</p>
                  {puedeOperar && !esFinalizado && <button onClick={() => undoCheckIn(i.id)} className="text-xs text-ink-400 hover:underline">restaurar</button>}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }

  // ════════ VISTA LISTA ════════
  return (
    <div className="mx-auto max-w-3xl px-6 py-8 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink-800">Eventos Privados</h1>
          <p className="text-ink-500 text-sm mt-1">Fiestas, reuniones corporativas y grupos con lista de invitados.</p>
        </div>
        <div className="flex gap-2">
          {puedeEditar && (
            <button onClick={() => { setEditTags([...tags]); setEditChecklist([...checklistTpl]); setShowConfig(true); }}
              className="btn-secondary flex items-center gap-2 text-sm" title="Configurar tags y checklist">
              <Settings className="size-4" />
            </button>
          )}
          {puedeEditar && (
            <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2 text-sm">
              <Plus className="size-4" /> Nuevo evento
            </button>
          )}
        </div>
      </div>

      {/* Aviso para recepción */}
      {!puedeEditar && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-700 flex items-center gap-2">
          <AlertCircle className="size-4 shrink-0" />
          Puedes marcar la llegada de invitados. Para crear o editar eventos, contacta a un supervisor.
        </div>
      )}

      {/* Modal de configuración */}
      {showConfig && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg text-ink-800">Configurar Eventos</h3>
              <button onClick={() => setShowConfig(false)} className="text-ink-400 hover:text-ink-700"><X className="size-5" /></button>
            </div>

            {/* Tags de observaciones */}
            <div>
              <h4 className="font-semibold text-ink-700 text-sm mb-2">Etiquetas de observaciones</h4>
              <p className="text-xs text-ink-400 mb-2">Aparecen como botones al crear/editar un evento.</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {editTags.map((t, i) => (
                  <span key={i} className="flex items-center gap-1 px-2.5 py-1 bg-brand-50 text-brand-700 rounded-full text-xs">
                    {t}
                    <button onClick={() => setEditTags(editTags.filter((_, idx) => idx !== i))} className="text-brand-400 hover:text-brand-700">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={nuevoTag} onChange={e => setNuevoTag(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && nuevoTag.trim()) { setEditTags([...editTags, nuevoTag.trim()]); setNuevoTag(""); } }}
                  className="input flex-1 text-sm" placeholder="Nueva etiqueta..." />
                <button onClick={() => { if (nuevoTag.trim()) { setEditTags([...editTags, nuevoTag.trim()]); setNuevoTag(""); } }}
                  className="btn-secondary text-sm"><Plus className="size-4" /></button>
              </div>
            </div>

            {/* Checklist template */}
            <div>
              <h4 className="font-semibold text-ink-700 text-sm mb-2">Items del checklist de preparación</h4>
              <p className="text-xs text-ink-400 mb-2">Se aplican automáticamente a cada evento nuevo.</p>
              <div className="space-y-1.5 mb-2">
                {editChecklist.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-ink-50 rounded-lg">
                    <ClipboardList className="size-3.5 text-ink-400" />
                    <span className="flex-1 text-sm text-ink-700">{c}</span>
                    <button onClick={() => setEditChecklist(editChecklist.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600"><X className="size-4" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={nuevoCheck} onChange={e => setNuevoCheck(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && nuevoCheck.trim()) { setEditChecklist([...editChecklist, nuevoCheck.trim()]); setNuevoCheck(""); } }}
                  className="input flex-1 text-sm" placeholder="Ej: Confirmar DJ, Preparar regalo..." />
                <button onClick={() => { if (nuevoCheck.trim()) { setEditChecklist([...editChecklist, nuevoCheck.trim()]); setNuevoCheck(""); } }}
                  className="btn-secondary text-sm"><Plus className="size-4" /></button>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowConfig(false)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={guardarConfig} disabled={saving} className="btn-primary flex-1">{saving ? "..." : "Guardar configuración"}</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {sucursales.map(s => (
          <button key={s.id} onClick={() => setSucursalFiltro(s.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${sucursalFiltro === s.id ? "bg-brand-600 text-white" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
            {s.nombre}
          </button>
        ))}
      </div>

      {/* Formulario nuevo */}
      {showForm && puedeEditar && (
        <div className="card p-5 space-y-4 border-2 border-brand-200">
          <h3 className="font-semibold text-ink-800">Nuevo evento privado</h3>
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Nombre del evento *</label>
            <input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} className="input w-full" placeholder="Ej: Fiesta Navidad Redesip 2026" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-ink-600 mb-1">Sucursal</label>
              <select value={form.sucursalId} onChange={e => setForm(f => ({ ...f, sucursalId: +e.target.value }))} className="input w-full">
                {sucursales.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select></div>
            <div><label className="block text-xs font-medium text-ink-600 mb-1">Zona / Sala</label>
              <select value={form.zonaId ?? ""} onChange={e => setForm(f => ({ ...f, zonaId: e.target.value ? +e.target.value : undefined }))} className="input w-full">
                <option value="">Sin zona específica</option>
                {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-ink-600 mb-1">Fecha *</label>
              <input type="date" value={form.fecha} min={hoyISO()} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} className="input w-full" /></div>
            <div><label className="block text-xs font-medium text-ink-600 mb-1">Inicio *</label>
              <input type="time" value={form.horaInicio} onChange={e => setForm(f => ({ ...f, horaInicio: e.target.value }))} className="input w-full" /></div>
            <div><label className="block text-xs font-medium text-ink-600 mb-1">Fin</label>
              <input type="time" value={form.horaFin} onChange={e => setForm(f => ({ ...f, horaFin: e.target.value }))} className="input w-full" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-ink-600 mb-1">Organizador</label>
              <input value={form.organizadorNombre} onChange={e => setForm(f => ({ ...f, organizadorNombre: e.target.value }))} className="input w-full text-sm" /></div>
            <div><label className="block text-xs font-medium text-ink-600 mb-1">Teléfono</label>
              <input value={form.organizadorTelefono} onChange={e => setForm(f => ({ ...f, organizadorTelefono: e.target.value }))} className="input w-full text-sm" /></div>
            <div><label className="block text-xs font-medium text-ink-600 mb-1">Email</label>
              <input value={form.organizadorEmail} onChange={e => setForm(f => ({ ...f, organizadorEmail: e.target.value }))} className="input w-full text-sm" /></div>
          </div>
          <div><label className="block text-xs font-medium text-ink-600 mb-1">Nota del evento</label>
            <input value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} className="input w-full text-sm" placeholder="Cena de gala, cumpleaños..." /></div>

          {/* Observaciones desde config */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-2">Observaciones del evento</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map(t => {
                const sel = form.observaciones.includes(t);
                return (
                  <button key={t} type="button"
                    onClick={() => setForm(f => ({ ...f, observaciones: sel ? f.observaciones.split("\n").filter(o => o !== t).join("\n") : (f.observaciones ? f.observaciones + "\n" + t : t) }))}
                    className={`text-xs px-2.5 py-1 rounded-full border ${sel ? "bg-brand-600 text-white border-brand-600" : "border-ink-300 text-ink-600 hover:border-brand-400"}`}>{t}</button>
                );
              })}
              {form.observaciones.split("\n").map(o => o.trim()).filter(o => o && !tags.includes(o)).map((o, i) => (
                <button key={`custom-${i}-${o}`} type="button" onClick={() => {
                  setForm(f => ({ ...f, observaciones: f.observaciones.split("\n").filter(line => line.trim() !== o).join("\n") }));
                }}
                  className="text-xs px-2.5 py-1 rounded-full border bg-brand-600 text-white border-brand-600 inline-flex items-center gap-1">
                  {o} <span className="opacity-70">×</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={nuevaObsForm} onChange={e => setNuevaObsForm(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && nuevaObsForm.trim()) {
                    e.preventDefault();
                    setForm(f => ({ ...f, observaciones: f.observaciones ? f.observaciones + "\n" + nuevaObsForm.trim() : nuevaObsForm.trim() }));
                    setNuevaObsForm("");
                  }
                }}
                placeholder="Escribir una observación manual..." className="input w-full text-sm" />
              <button type="button" onClick={() => {
                if (!nuevaObsForm.trim()) return;
                setForm(f => ({ ...f, observaciones: f.observaciones ? f.observaciones + "\n" + nuevaObsForm.trim() : nuevaObsForm.trim() }));
                setNuevaObsForm("");
              }} className="btn-secondary text-sm px-3">Añadir</button>
            </div>
          </div>

          <div><label className="block text-xs font-medium text-ink-600 mb-1">Notas internas <span className="font-normal text-ink-400">(equipo)</span></label>
            <textarea value={form.notasInternas} onChange={e => setForm(f => ({ ...f, notasInternas: e.target.value }))} rows={2} className="input w-full text-sm resize-none" placeholder="Detalles de pago, acuerdos especiales..." /></div>

          <Importador
            rawText={rawText} setRawText={setRawText}
            showPreview={showPreview} setShowPreview={setShowPreview}
            preview={preview} saving={saving}
            fileRef={fileRef} importarArchivo={importarArchivo}
            onImport={rows => setInvitadosTexto(rows.map(r => [r.nombre, r.email, r.telefono, r.acompanantes, r.notas].join(",")).join("\n"))} />
          {parsearTexto(invitadosTexto).length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm text-emerald-700">
              ✓ {parsearTexto(invitadosTexto).length} invitados listos para importar
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => { setShowForm(false); setForm({ ...FORM_EMPTY }); setInvitadosTexto(""); }} className="btn-secondary flex-1">Cancelar</button>
            <button onClick={crearEvento} disabled={saving || !form.nombre} className="btn-primary flex-1">{saving ? "Creando..." : "Crear evento"}</button>
          </div>
        </div>
      )}

      <input value={buscando} onChange={e => setBuscando(e.target.value)} className="input w-full" placeholder="Buscar evento u organizador..." />

      <div className="space-y-3">
        {filtrados.length === 0 ? (
          <div className="text-center py-12 text-ink-400 bg-ink-50 rounded-xl">
            <Calendar className="size-8 mx-auto mb-2 opacity-30" /><p className="text-sm">Sin eventos próximos</p>
          </div>
        ) : (
          filtrados.map(ev => {
            const inv = ev.invitados ?? [];
            const llegaron = inv.filter(i => i.estado === "llegó").length;
            const total = inv.length;
            return (
              <button key={ev.id} onClick={() => abrirEvento(ev.id)}
                className="w-full text-left card p-4 hover:border-brand-300 hover:shadow-md transition-all flex items-center gap-4">
                <div className="shrink-0 text-center bg-brand-50 rounded-xl p-3 min-w-[56px]">
                  <p className="text-lg font-bold text-brand-700 leading-none">{new Date(ev.fecha.slice(0,10) + "T00:00:00").getDate()}</p>
                  <p className="text-xs text-brand-500 capitalize">{new Date(ev.fecha.slice(0,10) + "T00:00:00").toLocaleDateString("es-VE", { month: "short" })}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-ink-800 truncate">{ev.nombre}</p>
                    {ev.estado === "finalizado" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500 shrink-0">Finalizado</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-ink-500 mt-0.5 flex-wrap">
                    <span>🕐 {ev.horaInicio}</span>
                    {ev.organizadorNombre && <span>👤 {ev.organizadorNombre}</span>}
                    <span><Users className="size-3 inline mr-0.5" />{ev.totalInvitados} personas</span>
                    {total > 0 && <span className="text-emerald-600">{llegaron}/{total} llegaron</span>}
                  </div>
                </div>
                <ChevronRight className="size-5 text-ink-300 shrink-0" />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
